import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Select, Button, Table, message, Spin, DatePicker, InputNumber, Checkbox, Input, Form, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ReloadOutlined, PlusOutlined, SaveOutlined, EditOutlined, CloseOutlined, UserAddOutlined } from '@ant-design/icons';
import * as subjectService from '../../services/subjectService';
import { MakeupService } from '../../services/makeupService';
import { buildAPIURL } from '../../config/apiConfig';
dayjs.extend(customParseFormat);

// API base
const apiBase = buildAPIURL('');

interface Course {
  KhoaHocID: string;
  TenKhoaHoc: string;
}

interface Subject {
  SubjectId?: string;
  SubjectID?: string;
  MonHocID?: string;
  TenMonHoc?: string;
  TenMH?: string;
  TongSoBuoi?: number; // Tổng số buổi học
  IsLocked?: boolean; // Khóa điểm danh môn học
}

interface ClassSection {
  LopHocPhanID?: string;
  MaLHP?: string;
  TenLopHP?: string;
  TenLHP?: string;
  TenLopHocPhan?: string;
  TenLop?: string;
}

interface Student {
  MaSV?: string;
  MaHS?: string;
  HoTen: string;
  NgaySinh?: string | Date; // Đồng nhất với AttendancePracticePage
  LopHocID?: string;
  LopSinhHoat?: string;
  LopSH?: string;
  Vang?: boolean;
  Muon?: number;
  BoTiet?: boolean;
  DiemTX?: number;
  KhongDat?: boolean;
  LyDo?: string;
}

interface LopHoc {
  LopHocID: string;
  TenLop: string;
}

const AttendanceTheoryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classSections, setClassSections] = useState<ClassSection[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedClassSection, setSelectedClassSection] = useState<string | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [session, setSession] = useState<string>('Sáng');
  const [soTietLT, setSoTietLT] = useState<number>(0);
  const [soTietTH, setSoTietTH] = useState<number>(0);
  const [suDungMayChieu, setSuDungMayChieu] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(true);
  const [attendanceData, setAttendanceData] = useState<Student[]>([]);
  const [tenBaiGiang, setTenBaiGiang] = useState<string>('');
  const [buoiSo, setBuoiSo] = useState<number | null>(null);

  const [buoiGiang, setBuoiGiang] = useState<string>('');
  const [ngayGiang, setNgayGiang] = useState<Dayjs>(dayjs());
  const [khoaHocId, setKhoaHocId] = useState<string>('');
  const [monHocId, setMonHocId] = useState<string>('');
  const [lopHocPhanId, setLopHocPhanId] = useState<string>('');
  const [lopHocs, setLopHocs] = useState<LopHoc[]>([]);
  const [isNewSession, setIsNewSession] = useState<boolean>(false);
  const [siSo, setSiSo] = useState<number>(0);


  // Modal states for TongSoBuoi
  const [tongSoBuoiModalVisible, setTongSoBuoiModalVisible] = useState<boolean>(false);
  const [selectedSubjectForTongSoBuoi, setSelectedSubjectForTongSoBuoi] = useState<Subject | null>(null);
  const [tongSoBuoiForm] = Form.useForm();

  const sessionOptions = [
    { label: 'Sáng', value: 'Sáng' },
    { label: 'Chiều', value: 'Chiều' },
    { label: 'Tối', value: 'Tối' }
  ];

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/courses`);
      const data = await res.json();
      console.log('[DEBUG] API /courses response:', data);
      console.log('[DEBUG] API /courses data type:', typeof data, 'isArray:', Array.isArray(data));

      // Kiểm tra nếu response có format {success: true, data: [...]}
      const coursesArray = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      console.log('[DEBUG] Courses array to set:', coursesArray);
      setCourses(coursesArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /courses:', err);
      setCourses([]);
    }
    setLoading(false);
  };

  const fetchSubjects = async (courseId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/subjects/by-khoahoc/${encodeURIComponent(courseId)}`);
      const data = await res.json();
      console.log('[DEBUG] API /subjects response:', data);

      // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
      const subjectsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] Subjects array to set:', subjectsArray);
      setSubjects(subjectsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /subjects:', err);
      setSubjects([]);
    }
    setLoading(false);
  };

  const fetchClassSections = async (courseId: string, subjectId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/lophocphan/by-monhoc/${encodeURIComponent(subjectId)}`);
      const data = await res.json();
      console.log('[DEBUG] API /lophocphan response:', data);

      // Kiểm tra nếu response có format {success: true, data: [...]} hoặc {data: [...]}
      const classSectionsArray = data.success && Array.isArray(data.data) ? data.data :
        (data.data && Array.isArray(data.data) ? data.data :
          (Array.isArray(data) ? data : []));
      console.log('[DEBUG] ClassSections array to set:', classSectionsArray);
      setClassSections(classSectionsArray);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch /lophocphan:', err);
      setClassSections([]);
    }
    setLoading(false);
  };

  const fetchStudents = async (classSectionId: string) => {
    setLoading(true);
    try {
      console.log('[DEBUG] AttendanceTheoryPage: Fetching students for classSectionId:', classSectionId);

      // Sử dụng MakeupService để lấy dữ liệu sinh viên từ DB2
      const studentArray = await MakeupService.getStudentsByClassSection({ malophp: classSectionId });
      console.log('[DEBUG] MakeupService.getStudentsByClassSection response:', studentArray);

      // Sắp xếp sinh viên theo Mã SV tăng dần
      const sortedStudentArray = studentArray.sort((a: Student, b: Student) => {
        const maA = (a.MaHS || a.MaSV || '').toString();
        const maB = (b.MaHS || b.MaSV || '').toString();
        return maA.localeCompare(maB);
      });

      setStudents(sortedStudentArray);
      // Helper: format DOB for display as dd/MM/yyyy
      const formatDOB = (raw: any): string | null => {
        if (raw === null || raw === undefined || raw === '') return null;
        if (typeof raw === 'string') {
          const s = raw.trim();
          if (!s) return null;
          // Try common VN/EU formats first
          const tryFormats = ['D/M/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/M/D', 'D-M-YYYY', 'DD-MM-YYYY'];
          for (const f of tryFormats) {
            const d = dayjs(s.replace(/[.]/g, '/').replace(/-/g, '/'), f, true);
            if (d.isValid()) return d.format('DD/MM/YYYY');
          }
          // Fallback parse
          const d = dayjs(s);
          return d.isValid() ? d.format('DD/MM/YYYY') : null;
        }
        // Date instance
        const d = dayjs(raw);
        return d.isValid() ? d.format('DD/MM/YYYY') : null;
      };

      setAttendanceData(sortedStudentArray.map((sv: Student) => {
        // Debug NgaySinh cho từng sinh viên với chi tiết hơn
        console.log('[DEBUG] Student NgaySinh processing:', {
          MaHS: sv.MaHS,
          HoTen: sv.HoTen,
          NgaySinhOriginal: sv.NgaySinh,
          NgaySinhType: typeof sv.NgaySinh,
          NgaySinhString: String(sv.NgaySinh),
          NgaySinhJSON: JSON.stringify(sv.NgaySinh),
          NgaySinhIsValid: sv.NgaySinh ? dayjs(sv.NgaySinh).isValid() : false,
          NgaySinhParsed: sv.NgaySinh ? dayjs(sv.NgaySinh).toString() : 'N/A'
        });

        return {
          ...sv,
          // Giữ nguyên NgaySinh gốc, để render function xử lý
          // Chuẩn hóa Ngày sinh để HIỂN THỊ nhất quán dd/MM/YYYY (không ảnh hưởng khi lưu, sẽ re-parse khi save)
          NgaySinh: formatDOB(sv.NgaySinh) || undefined,
          Vang: false,
          Muon: 0,
          BoTiet: false,
          DiemTX: 0,
          KhongDat: false,
          LyDo: '',
        };
      }));
      // Sĩ số = tổng số sinh viên của lớp học phần (không phải số sinh viên có điểm danh)
      setSiSo(sortedStudentArray.length);
      console.log('[DEBUG] setSiSo (tổng sinh viên lớp học phần):', sortedStudentArray.length);
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch students từ MakeupService:', err);
      setStudents([]);
      setAttendanceData([]);
    }
    setLoading(false);
  };

  const fetchAttendance = async () => {
    if (!selectedClassSection || !date || !session) return;
    setLoading(true);
    try {
      // GET theo ngày/buổi (backend đảm bảo chỉ 1 phiên), không bắt buộc buoiSo
      const url = `${apiBase}/attendance-theory?classSectionId=${selectedClassSection}&date=${date.format('YYYY-MM-DD')}&session=${session}`;

      const res = await fetch(url);
      const data = await res.json();

      console.log('[DEBUG][fetchAttendance] data:', data);
      console.log('[DEBUG][fetchAttendance] URL called:', url);

      if (data && data.hasExistingAttendance) {
        // Có dữ liệu điểm danh đã tồn tại
        console.log('[DEBUG] Đã có dữ liệu điểm danh tồn tại, cần merge với danh sách đầy đủ');

        // Lấy danh sách đầy đủ sinh viên từ MakeupService
        try {
          const studentsResponse = await MakeupService.getStudentsByClassSection({ malophp: selectedClassSection });
          const allStudents = Array.isArray(studentsResponse) ? studentsResponse : [];

          // Merge dữ liệu: tất cả sinh viên + dữ liệu điểm danh (nếu có)
          const attendanceDetails = Array.isArray(data.students) ? data.students : [];

          const mergedStudents = allStudents.map((student: any) => {
            // Tìm dữ liệu điểm danh cho sinh viên này
            const detail = attendanceDetails.find((d: any) => (d.MaSV || d.MaHS) === student.MaHS);
            const formatDOB = (raw: any): string | null => {
              if (raw === null || raw === undefined || raw === '') return null;
              const s = String(raw).trim();
              const tryFormats = ['D/M/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/M/D', 'D-M-YYYY', 'DD-MM-YYYY'];
              const normalized = s.replace(/[.]/g, '/').replace(/-/g, '/');
              for (const f of tryFormats) {
                const d = dayjs(normalized, f, true);
                if (d.isValid()) return d.format('DD/MM/YYYY');
              }
              const d = dayjs(s);
              return d.isValid() ? d.format('DD/MM/YYYY') : null;
            };

            if (detail) {
              // Nếu có dữ liệu điểm danh, ưu tiên dữ liệu đã lưu
              return {
                ...student,
                MaHS: student.MaHS,
                MaSV: student.MaHS,
                HoTen: student.HoTen,
                NgaySinh: formatDOB(student.NgaySinh ?? detail.NgaySinh) || null,
                Vang: Boolean(detail.Vang),
                Muon: Number(detail.Muon) || 0,
                BoTiet: Boolean(detail.BoTiet),
                DiemTX: Number(detail.DiemTX) || 0,
                KhongDat: Boolean(detail.KhongDat),
                LyDo: detail.LyDo || ''
              };
            }

            // Nếu chưa có dữ liệu điểm danh, khởi tạo với giá trị mặc định
            return {
              ...student,
              MaHS: student.MaHS,
              MaSV: student.MaHS,
              HoTen: student.HoTen,
              NgaySinh: formatDOB(student.NgaySinh) || null,
              Vang: false,
              Muon: 0,
              BoTiet: false,
              DiemTX: 0,
              KhongDat: false,
              LyDo: ''
            };
          });

          // Cập nhật state với dữ liệu đã merge
          setSoTietLT(data.SoTietLT || 0);
          setSoTietTH(data.SoTietTH || 0);
          setSuDungMayChieu(data.SuDungMayChieu || false);
          setAttendanceData(mergedStudents);
          setTenBaiGiang(data.TenBaiGiang || '');
          setBuoiSo(data.BuoiSo || null);
          setBuoiGiang(data.BuoiGiang || '');
          setNgayGiang(data.NgayGiang ? dayjs(data.NgayGiang) : dayjs());
          setKhoaHocId(data.KhoaHocID || '');
          setLopHocPhanId(data.LopHocPhanID || '');
          setSiSo(data.SiSo || mergedStudents.length);
          setEditMode(false);
          setIsNewSession(false);

          console.log('[DEBUG] Merged students:', mergedStudents);
        } catch (err) {
          console.error('[DEBUG] Lỗi lấy danh sách sinh viên từ MakeupService:', err);
          // Fallback: chỉ hiển thị sinh viên có trong AttendanceTheoryDetail
          setAttendanceData(data.students || []);
          setSiSo(data.SiSo || 0);
          setEditMode(false);
          setIsNewSession(false);
        }
      } else {
        // Chưa có dữ liệu điểm danh, tạo mới
        setEditMode(true);
        setIsNewSession(true);
        setSoTietLT(0);
        setSoTietTH(0);
        setSuDungMayChieu(false);
        setAttendanceData([]);
        setTenBaiGiang('');
        setBuoiSo(null);
        setBuoiGiang('');
        setNgayGiang(dayjs());
        setKhoaHocId('');
        setMonHocId('');
        setLopHocPhanId('');
        setSiSo(0);
      }
    } catch (err) {
      console.error('[DEBUG] Lỗi fetch attendance:', err);
      setEditMode(true);
      setIsNewSession(true);
      setSoTietLT(0);
      setSoTietTH(0);
      setSuDungMayChieu(false);
      setAttendanceData([]);
      setTenBaiGiang('');
      setBuoiSo(null);
      setBuoiGiang('');
      setNgayGiang(dayjs());
      setKhoaHocId('');
      setMonHocId('');
      setLopHocPhanId('');
      setSiSo(0);
    }
    setLoading(false);
  };

  const saveAttendance = async () => {
    // Validation: Kiểm tra Buổi số bắt buộc nhập
    if (!buoiSo || buoiSo <= 0) {
      message.error('Vui lòng nhập Buổi số trước khi lưu!');
      return;
    }

    // Validation: Không vượt Tổng số buổi của môn học (nếu có)
    try {
      if (selectedSubject && subjects.length > 0) {
        const currentSubject = subjects.find(s => String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject);
        const maxBuoi = currentSubject?.TongSoBuoi ? Number(currentSubject.TongSoBuoi) : null;
        if (maxBuoi && buoiSo > maxBuoi) {
          message.error(`Buổi số ${buoiSo} vượt quá Tổng số buổi (${maxBuoi}) của môn học.`);
          return;
        }
      }
    } catch { }

    // Validation: Kiểm tra có dữ liệu sinh viên
    if (!attendanceData || attendanceData.length === 0) {
      message.error('Vui lòng lấy danh sách sinh viên trước khi lưu!');
      return;
    }

    // Validation: Số tiết LT tối đa 4, TH tối đa 3
    if (soTietLT > 4) {
      message.error('Số tiết lý thuyết không được vượt quá 4 tiết!');
      return;
    }
    if (soTietTH > 3) {
      message.error('Số tiết thực hành không được vượt quá 3 tiết!');
      return;
    }

    // Validation: Tổng số tiết không quá 4 nếu có cả LT và TH
    const totalTiet = soTietLT + soTietTH;
    if (soTietLT > 0 && soTietTH > 0 && totalTiet > 4) {
      message.error('Tổng số tiết lý thuyết và thực hành không được vượt quá 4 tiết!');
      return;
    }

    // Validation: Phải có ít nhất 1 số tiết
    if (soTietLT === 0 && soTietTH === 0) {
      message.error('Phải nhập ít nhất một trong hai: Số tiết LT hoặc Số tiết TH!');
      return;
    }

    setLoading(true);
    try {
      // Lấy thông tin giảng viên từ localStorage hoặc sessionStorage
      let currentUser = localStorage.getItem('username') || sessionStorage.getItem('username') ||
        localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser') || '';

      // Kiểm tra trong quanly_truonghoc_user
      if (!currentUser) {
        try {
          const userDataStr = localStorage.getItem('quanly_truonghoc_user') || sessionStorage.getItem('quanly_truonghoc_user');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            currentUser = userData.Username || userData.username || '';
          }
        } catch (e) {
          console.error('[DEBUG] Error parsing user data:', e);
        }
      }

      // Chuyển đổi buổi giảng thành ký tự đầu tiên
      const buoiGiangCode = session.charAt(0).toUpperCase(); // S, C, T

      console.log('[DEBUG][saveAttendance] currentUser:', currentUser);
      console.log('[DEBUG][saveAttendance] buoiGiangCode:', buoiGiangCode);
      console.log('[DEBUG][saveAttendance] localStorage keys:', Object.keys(localStorage));
      console.log('[DEBUG][saveAttendance] sessionStorage keys:', Object.keys(sessionStorage));

      // Chuẩn hóa dữ liệu sinh viên trước khi gửi
      const normalizedAttendanceData = attendanceData.map((student: any) => {
        // Chuẩn hóa LopSinhHoat - Lấy 10 ký tự đầu tiên từ MaHS
        const maHS = student.MaHS || student.MaSV || '';
        const lopSinhHoat = maHS.toString().substring(0, 10);

        return {
          ...student,
          // Đảm bảo các trường bắt buộc có giá trị hợp lệ
          MaSV: student.MaSV || student.MaHS || '',
          MaHS: student.MaHS || student.MaSV || '',
          HoTen: student.HoTen || '',
          NgaySinh: (() => {
            if (!student.NgaySinh) return null;
            if (student.NgaySinh === 'Invalid Date') return null;
            const s = String(student.NgaySinh).trim();
            const tryFormats = ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'YYYY/M/D', 'DD-MM-YYYY', 'D-M-YYYY'];
            for (const f of tryFormats) {
              const d = dayjs(s.replace(/[.]/g, '/').replace(/-/g, '/'), f, true);
              if (d.isValid()) return d.format('YYYY-MM-DD');
            }
            const d = dayjs(s);
            return d.isValid() ? d.format('YYYY-MM-DD') : null;
          })(),
          Vang: Boolean(student.Vang),
          Muon: Number(student.Muon) || 0,
          BoTiet: Boolean(student.BoTiet),
          DiemTX: Number(student.DiemTX) || 0,
          KhongDat: Boolean(student.KhongDat),
          LyDo: student.LyDo || '',
          LopSinhHoat: lopSinhHoat // Thêm trường LopSinhHoat được chuẩn hóa
        };
      });

      console.log('[DEBUG][saveAttendance] normalizedAttendanceData:', normalizedAttendanceData);

      const res = await fetch(`${apiBase}/attendance-theory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          classSectionId: selectedClassSection,
          date: date.format('YYYY-MM-DD'),
          session,
          buoiSo,
          tenBaiGiang,
          soTietLT,
          soTietTH,
          suDungMayChieu,
          students: normalizedAttendanceData, // Sử dụng dữ liệu đã chuẩn hóa
          khoaHocId: selectedCourse,
          monHocId: selectedSubject,
          lopHocPhanId: selectedClassSection,
          ngayGiang: date.format('YYYY-MM-DD'),
          buoiGiang: buoiGiangCode,
          siSo,
          giangVien: currentUser,
          isEditMode: editMode && !isNewSession // Gửi flag để backend biết đây là edit hay create
        })
      });

      const data = await res.json();
      console.log('[DEBUG][saveAttendance] Response status:', res.status);
      console.log('[DEBUG][saveAttendance] Response data:', data);

      if (res.ok && data.success) {
        message.success(data.message || 'Lưu dữ liệu thành công!');
        setEditMode(false);
        setIsNewSession(false);
        // Giữ nguyên BuoiSo và SiSo nếu backend không trả về giá trị hợp lệ
        if (typeof data.BuoiSo !== 'undefined' && data.BuoiSo !== null) setBuoiSo(data.BuoiSo);
        // Nếu backend trả về SiSo hợp lệ thì cập nhật, còn không thì giữ nguyên
        if (typeof data.SiSo !== 'undefined' && data.SiSo !== null) setSiSo(data.SiSo);
      } else {
        console.error('[DEBUG][saveAttendance] Error response:', data);
        const errMsg = data.error || data.message || 'Lưu điểm danh thất bại!';
        message.error(errMsg);
      }
    } catch (e) {
      console.error('[DEBUG][saveAttendance] Exception:', e);
      message.error('Lưu điểm danh thất bại!');
    }
    setLoading(false);
  };

  const resetAttendanceForm = () => {
    setSelectedCourse(null);
    setSelectedSubject(null);
    setSelectedClassSection(null);
    setDate(dayjs());
    setSession('Sáng');
    setSoTietLT(0);
    setSoTietTH(0);
    setSuDungMayChieu(false);
    setAttendanceData([]);
    setTenBaiGiang('');
    setBuoiSo(null);
    setBuoiGiang('');
    setNgayGiang(dayjs());
    setKhoaHocId('');
    setMonHocId('');
    setLopHocPhanId('');
    setEditMode(true);
    setIsNewSession(true);
    setSiSo(0);
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!selectedCourse) {
        setLopHocs([]);
        return;
      }

      try {
        // Lấy lớp học theo KhoaHocID
        const res = await fetch(`${apiBase}/classes?khoahocId=${encodeURIComponent(selectedCourse)}`);
        const data = await res.json();
        const classesArray = data.success && Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
        setLopHocs(classesArray);
        console.log('[DEBUG] API /classes by KhoaHocID:', classesArray);
      } catch (err) {
        console.error('[DEBUG] Lỗi fetch /classes by KhoaHocID:', err);
        setLopHocs([]);
      }
    };
    fetchClasses();
  }, [selectedCourse]); // Thay đổi dependency từ [] thành [selectedCourse]

  useEffect(() => {
    if (selectedCourse) fetchSubjects(selectedCourse);
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedSubject) fetchClassSections(selectedCourse, selectedSubject);
  }, [selectedCourse, selectedSubject]);

  // Check TongSoBuoi and IsLocked when subject is selected
  useEffect(() => {
    if (selectedSubject && subjects.length > 0) {
      const currentSubject = subjects.find(s =>
        String(s.SubjectId || s.SubjectID || s.MonHocID) === selectedSubject
      );

      if (currentSubject) {
        // Kiểm tra môn học có bị khóa không
        if (currentSubject.IsLocked) {
          message.warning('Môn học này đã bị khóa điểm danh. Không thể thực hiện điểm danh.');
          setSelectedSubject(null);
          return;
        }

        // Kiểm tra TongSoBuoi
        if (!currentSubject.TongSoBuoi || currentSubject.TongSoBuoi === 0) {
          // Hiển thị modal yêu cầu nhập số buổi
          setSelectedSubjectForTongSoBuoi(currentSubject);
          setTongSoBuoiModalVisible(true);
        }
      }
    }
  }, [selectedSubject, subjects]);

  useEffect(() => {
    if (isNewSession) return;
    // Chỉ tự động load nếu có đủ thông tin, bao gồm cả buoiSo
    if (selectedClassSection && date && session && buoiSo && buoiSo > 0) {
      fetchAttendance();
    }
  }, [selectedClassSection, date, session, buoiSo, isNewSession]);

  useEffect(() => {
    resetAttendanceForm();
  }, []);

  const handleGetStudents = (): void => {
    if (!selectedClassSection) {
      message.warning('Vui lòng chọn lớp học phần!');
      return;
    }

    // LUÔN lấy danh sách sinh viên mới từ MakeupService
    // Không quan tâm đến buoiSo - đây là chức năng lấy danh sách, không phải load điểm danh
    console.log('[DEBUG] Lấy danh sách sinh viên mới cho lớp học phần:', selectedClassSection);
    fetchStudents(selectedClassSection);
  };

  const handleAttendanceChange = (index: number, field: string, value: any) => {
    setAttendanceData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleEdit = () => setEditMode(true);

  const handleClose = () => {
    resetAttendanceForm();
    window.history.back();
  };

  // Handle TongSoBuoi update
  const handleTongSoBuoiSubmit = async () => {
    try {
      const values = await tongSoBuoiForm.validateFields();
      const tongSoBuoi = values.tongSoBuoi;

      if (!selectedSubjectForTongSoBuoi) return;

      const subjectId = selectedSubjectForTongSoBuoi.SubjectId ||
        selectedSubjectForTongSoBuoi.SubjectID ||
        selectedSubjectForTongSoBuoi.MonHocID;

      if (!subjectId) {
        message.error('Không thể xác định ID môn học');
        return;
      }

      // Gọi API cập nhật
      await subjectService.updateSubjectTongSoBuoi(subjectId, tongSoBuoi);

      // Cập nhật local state
      setSubjects(prev => prev.map(subject =>
        (subject.SubjectId || subject.SubjectID || subject.MonHocID) === subjectId
          ? { ...subject, TongSoBuoi: tongSoBuoi }
          : subject
      ));

      message.success('Cập nhật tổng số buổi học thành công!');
      setTongSoBuoiModalVisible(false);
      tongSoBuoiForm.resetFields();
      setSelectedSubjectForTongSoBuoi(null);

    } catch (error) {
      console.error('Error updating TongSoBuoi:', error);
      message.error('Không thể cập nhật tổng số buổi học');
    }
  };

  const handleTongSoBuoiCancel = () => {
    setTongSoBuoiModalVisible(false);
    tongSoBuoiForm.resetFields();
    setSelectedSubjectForTongSoBuoi(null);
    // Reset selected subject to prevent continuous loop
    setSelectedSubject(null);
  };

  const normalizeLopHocID = (id: string | undefined) => (id || '').toString().trim();

  // Hàm lấy LopHocID từ MaHS (10 ký tự đầu tiên)
  const getLopHocIDFromMaHS = (maHS: string | undefined) => {
    if (!maHS) return '';
    return maHS.toString().substring(0, 10);
  };

  const lopHocMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(lopHocs)) {
      lopHocs.forEach(lh => {
        map[normalizeLopHocID(lh.LopHocID)] = lh.TenLop;
      });
    }
    return map;
  }, [lopHocs]);

  const columns: ColumnsType<Student> = [
    {
      title: 'Mã SV',
      dataIndex: 'MaHS',
      key: 'MaHS',
      sorter: (a: Student, b: Student) => (a.MaHS || a.MaSV || '').localeCompare(b.MaHS || b.MaSV || ''),
      sortDirections: ['ascend', 'descend'],
      filteredValue: null
    },
    {
      title: 'Họ và tên',
      dataIndex: 'HoTen',
      key: 'HoTen',
      sorter: (a: Student, b: Student) => (a.HoTen || '').localeCompare(b.HoTen || ''),
      sortDirections: ['ascend', 'descend'],
      filters: Array.isArray(attendanceData) ? Array.from(new Set(attendanceData.map(sv => sv.HoTen).filter((name): name is string => Boolean(name)))).map(name => ({ text: name, value: name })) : [],
      onFilter: (value, record: Student) => record.HoTen === value
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'NgaySinh',
      key: 'NgaySinh',
      sorter: (a: Student, b: Student) => {
        const formats = ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'YYYY/M/D', 'DD-MM-YYYY', 'D-M-YYYY'];
        const parse = (val: any) => {
          if (!val && val !== 0) return 0;
          const s = String(val).trim().replace(/[.]/g, '/').replace(/-/g, '/');
          for (const f of formats) {
            const d = dayjs(s, f, true);
            if (d.isValid()) return d.valueOf();
          }
          const d = dayjs(s);
          return d.isValid() ? d.valueOf() : 0;
        };
        return parse(a.NgaySinh) - parse(b.NgaySinh);
      },
      sortDirections: ['ascend', 'descend'],
      render: (text: string | Date) => {
        if (!text) return <span style={{ color: '#999' }}>Chưa có</span>;
        const s = typeof text === 'string' ? text : String(text);
        const tryFormats = ['D/M/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/M/D', 'D-M-YYYY', 'DD-MM-YYYY'];
        const normalized = s.trim().replace(/[.]/g, '/').replace(/-/g, '/');
        for (const f of tryFormats) {
          const d = dayjs(normalized, f, true);
          if (d.isValid()) return d.format('DD/MM/YYYY');
        }
        const d = dayjs(s);
        return d.isValid() ? d.format('DD/MM/YYYY') : <span style={{ color: '#999' }}>Chưa có</span>;
      }
    },
    {
      title: 'Vắng',
      dataIndex: 'Vang',
      key: 'Vang',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.Vang || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'Vang', e.target.checked)}
        />
      )
    },
    {
      title: 'Muộn',
      dataIndex: 'Muon',
      key: 'Muon',
      render: (_: any, __: Student, idx: number) => (
        <InputNumber
          min={0}
          value={attendanceData[idx]?.Muon || 0}
          disabled={!editMode}
          onChange={v => handleAttendanceChange(idx, 'Muon', v || 0)}
          style={{ width: 60 }}
        />
      )
    },
    {
      title: 'Bỏ tiết',
      dataIndex: 'BoTiet',
      key: 'BoTiet',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.BoTiet || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'BoTiet', e.target.checked)}
        />
      )
    },
    {
      title: 'Điểm TX',
      dataIndex: 'DiemTX',
      key: 'DiemTX',
      render: (_: any, __: Student, idx: number) => (
        <InputNumber
          min={0}
          max={10}
          step={0.1}
          value={attendanceData[idx]?.DiemTX || 0}
          disabled={!editMode}
          onChange={v => handleAttendanceChange(idx, 'DiemTX', v || 0)}
          style={{ width: 60 }}
        />
      )
    },
    {
      title: 'Không đạt',
      dataIndex: 'KhongDat',
      key: 'KhongDat',
      render: (_: any, __: Student, idx: number) => (
        <Checkbox
          checked={attendanceData[idx]?.KhongDat || false}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'KhongDat', e.target.checked)}
        />
      )
    },
    {
      title: 'Lý do',
      dataIndex: 'LyDo',
      key: 'LyDo',
      render: (_: any, __: Student, idx: number) => (
        <Input
          value={attendanceData[idx]?.LyDo || ''}
          disabled={!editMode}
          onChange={e => handleAttendanceChange(idx, 'LyDo', e.target.value)}
          style={{ width: 120 }}
        />
      )
    },
    {
      title: 'Lớp sinh hoạt',
      dataIndex: 'MaHS',
      key: 'LopSinhHoat',
      sorter: (a: Student, b: Student) => {
        const lopHocIdA = getLopHocIDFromMaHS(a.MaHS || a.MaSV);
        const lopHocIdB = getLopHocIDFromMaHS(b.MaHS || b.MaSV);
        const tenA = lopHocMap[lopHocIdA] || lopHocIdA;
        const tenB = lopHocMap[lopHocIdB] || lopHocIdB;
        return tenA.localeCompare(tenB);
      },
      filters: Array.isArray(attendanceData) ? Array.from(new Set(
        attendanceData
          .map(sv => {
            const lopHocId = getLopHocIDFromMaHS(sv.MaHS || sv.MaSV);
            return lopHocMap[lopHocId] || lopHocId;
          })
          .filter((name): name is string => Boolean(name) && typeof name === 'string')
      )).map(name => ({ text: name, value: name })) : [],
      onFilter: (value, record: Student) => {
        const lopHocId = getLopHocIDFromMaHS(record.MaHS || record.MaSV);
        return (lopHocMap[lopHocId] || lopHocId) === value;
      },
      render: (maHS: string) => {
        const lopHocId = getLopHocIDFromMaHS(maHS);
        return lopHocMap[lopHocId] || lopHocId || 'N/A';
      }
    },
  ];

  return (
    <div style={{ padding: 24, background: '#f6f7fb', minHeight: '100vh' }}>
      <h2 style={{ fontWeight: 700, marginBottom: 16 }}>Điểm danh lý thuyết</h2>
      <Button type="primary" icon={<ReloadOutlined />} onClick={resetAttendanceForm} style={{ marginBottom: 12 }}>
        Tạo mới phiếu điểm danh
      </Button>

      <Card style={{ marginBottom: 24 }}>
        {/* Dòng 1: Khóa học, Môn học, Lớp học phần */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={8}>
            <Form.Item label="Khóa học" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn khóa học"
                value={selectedCourse}
                onChange={setSelectedCourse}
                options={Array.isArray(courses) ? courses.map(c => ({ value: c.KhoaHocID, label: `${c.KhoaHocID} - ${c.TenKhoaHoc}` })) : []}
                allowClear
                disabled={!editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Môn học" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn môn học"
                value={selectedSubject}
                onChange={setSelectedSubject}
                options={Array.isArray(subjects) ? subjects.map(s => ({
                  value: String(s.SubjectId || s.SubjectID || s.MonHocID),
                  label: `${s.SubjectId || s.SubjectID || s.MonHocID} - ${s.TenMonHoc || s.TenMH}`
                })) : []}
                allowClear
                disabled={!selectedCourse || !editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Lớp học phần" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn lớp học phần"
                value={selectedClassSection}
                onChange={setSelectedClassSection}
                options={Array.isArray(classSections) ? classSections.map((cs: any) => ({
                  value: cs.MaLHP || cs.LopHocPhanID,
                  label: `${cs.MaLHP || cs.LopHocPhanID} - ${cs.TenLHP || cs.TenLopHP || cs.TenLopHocPhan || cs.TenLop}`
                })) : []}
                allowClear
                disabled={!selectedSubject || !editMode}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Dòng 2: Ngày giảng, Buổi giảng, Buổi số, Số tiết LT, Số tiết TH, Sử dụng máy chiếu + Sĩ số */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={4}>
            <Form.Item label="Ngày giảng" style={{ marginBottom: 0 }}>
              <DatePicker
                style={{ width: '100%' }}
                value={date}
                onChange={(d) => setDate(d || dayjs())}
                disabled={!editMode}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Buổi giảng" style={{ marginBottom: 0 }}>
              <Select
                style={{ width: 80 }}
                value={session}
                onChange={setSession}
                options={sessionOptions}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Buổi số" style={{ marginBottom: 0 }}>
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                value={buoiSo}
                onChange={(v) => { setBuoiSo(v as number | null); }}
                placeholder="Buổi số"
                disabled={!editMode}
              />

            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Số tiết LT" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                max={4}
                style={{ width: '100%' }}
                placeholder="LT"
                value={soTietLT}
                onChange={(v) => {
                  const newValue = v || 0;
                  // Kiểm tra tổng số tiết nếu có cả LT và TH
                  if (soTietTH > 0 && newValue > 0 && (newValue + soTietTH) > 4) {
                    message.warning('Tổng số tiết LT và TH không được vượt quá 4!');
                    return;
                  }
                  setSoTietLT(newValue);
                }}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Số tiết TH" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                max={3}
                style={{ width: '100%' }}
                placeholder="TH"
                value={soTietTH}
                onChange={(v) => {
                  const newValue = v || 0;
                  // Kiểm tra tổng số tiết nếu có cả LT và TH
                  if (soTietLT > 0 && newValue > 0 && (soTietLT + newValue) > 4) {
                    message.warning('Tổng số tiết LT và TH không được vượt quá 4!');
                    return;
                  }
                  setSoTietTH(newValue);
                }}
                disabled={!editMode}
              />
            </Form.Item>
          </Col>
          <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
            <Form.Item label="Sĩ số" style={{ width: '100%', marginBottom: 0, marginLeft: 16 }}>
              <Input value={siSo.toString()} disabled style={{ width: 80 }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Dòng 3: Tên bài giảng + Sử dụng máy chiếu */}
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col span={20} style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ minWidth: 100 }}>Tên bài giảng:</span>
            <Input
              style={{ flex: 1, marginLeft: 8 }}
              value={tenBaiGiang}
              onChange={e => setTenBaiGiang(e.target.value)}
              placeholder="Nhập tên bài giảng"
              disabled={!editMode}
            />
          </Col>
          <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={suDungMayChieu}
              onChange={e => setSuDungMayChieu(e.target.checked)}
              disabled={!editMode}
              style={{ marginBottom: 0 }}
            >
              Sử dụng máy chiếu
            </Checkbox>
          </Col>
        </Row>

        {/* Nút thao tác */}
        <Row gutter={16}>
          <Col span={6}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleGetStudents}
              loading={loading}
              style={{ width: '100%' }}
              disabled={!editMode}
            >
              Lấy danh sách sinh viên
            </Button>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveAttendance}
              loading={loading}
              style={{ width: '100%' }}
              disabled={!editMode || attendanceData.length === 0}
            >
              Lưu
            </Button>
          </Col>
          <Col span={6}>
            <Button
              icon={<EditOutlined />}
              onClick={handleEdit}
              style={{ width: '100%' }}
              disabled={editMode || attendanceData.length === 0}
            >
              Sửa
            </Button>
          </Col>
          <Col span={6}>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={handleClose}
              style={{ width: '100%' }}
            >
              Đóng
            </Button>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={attendanceData}
          rowKey={(record: Student) => record.MaSV || record.MaHS || Math.random().toString()}
          pagination={false}
          bordered
          scroll={{ x: 'max-content' }}
        />
      </Spin>

      {/* Modal nhập tổng số buổi học */}
      <Modal
        title="Nhập tổng số buổi học"
        open={tongSoBuoiModalVisible}
        onOk={handleTongSoBuoiSubmit}
        onCancel={handleTongSoBuoiCancel}
        okText="Xác nhận"
        cancelText="Bỏ qua"
        maskClosable={false}
        closable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>Môn học:</strong> {selectedSubjectForTongSoBuoi?.TenMonHoc || selectedSubjectForTongSoBuoi?.TenMH}</p>
          <p><strong>Mã môn học:</strong> {selectedSubjectForTongSoBuoi?.SubjectId || selectedSubjectForTongSoBuoi?.SubjectID || selectedSubjectForTongSoBuoi?.MonHocID}</p>
          <p style={{ color: '#ff4d4f' }}>
            Môn học này chưa có thông tin tổng số buổi học. Vui lòng nhập để tiếp tục điểm danh.
          </p>
        </div>

        <Form form={tongSoBuoiForm} layout="vertical">
          <Form.Item
            name="tongSoBuoi"
            label="Tổng số buổi học"
            rules={[
              { required: true, message: 'Vui lòng nhập tổng số buổi học' },
              { type: 'number', min: 1, max: 100, message: 'Số buổi phải từ 1 đến 100' }
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder="Nhập tổng số buổi học (1-100)"
              style={{ width: '100%' }}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AttendanceTheoryPage; 